/**
 * Kontingent-Auskunft der Instanz — FROZEN CONTRACT (Post #4713, Cloud 16.08.):
 *
 *   GET /api/tenant/usage → { included_units, used_units, resets_at, throttled }
 *
 * Fork-unabhängig: Die QUELLE von used_units (grobe Einheiten / AiApi /
 * Provider-Usage) entscheidet Alex' Abrechnungswahl — die FORM steht. Die UI
 * hängt nur an der Form.
 *
 * SELBST-GATEND: Instanzen ohne den Endpunkt (heute: alle — der Gap-1-Bau
 * liegt bei Cloud) antworten 404 → Hook liefert null → UI zeigt NICHTS.
 * Kein Feature-Flag, keine Konfiguration; die Anzeige erscheint von selbst,
 * sobald eine Instanz den Vertrag erfüllt. Dasselbe Muster wie useInstanceMode.
 */
import { useEffect, useState } from 'react';
import { authHeader } from '../api/client';
import { instanceReady, instanceHas } from './useInstanceMode';

export interface TenantUsage {
  included_units: number;
  used_units: number;
  resets_at: string;
  throttled: boolean;
}

/** Anzeige-Zustand aus den Vertragswerten — pur, damit testbar. */
export type UsageLevel = 'ok' | 'warn' | 'throttled';

export function usageLevel(u: Pick<TenantUsage, 'included_units' | 'used_units' | 'throttled'>): UsageLevel {
  if (u.throttled) return 'throttled';
  if (u.included_units > 0 && u.used_units / u.included_units >= 0.8) return 'warn';
  return 'ok';
}

export function usagePercent(u: Pick<TenantUsage, 'included_units' | 'used_units'>): number {
  if (u.included_units <= 0) return 0;
  return Math.min(100, Math.round((u.used_units / u.included_units) * 100));
}

const POLL_MS = 120_000; // Verbrauch ändert sich in Minuten, nicht Sekunden.

// Der Endpunkt ist der EINGEFRORENE Vertrag aus #4713 (Gap 1) — gebaut wird er
// erst nach Alex' Abrechnungs-Entscheidung. Bis dahin antwortet JEDE Instanz
// 404, auch die Föderation (verifiziert 18.08.: cloud2 + cloud-api = 404).
// Deshalb: EINMAL fragen, und bei 404 für diese Sitzung dauerhaft schweigen —
// statt alle zwei Minuten ein vorhersehbares 404 in die Konsole zu schreiben.
// Kein Feature-Flag, keine Konfiguration: Sobald eine Instanz den Vertrag
// erfüllt, trägt die erste Antwort die Anzeige (Reload genügt).
let endpointMissing = false;

// Und über den Reload hinaus: Ein 404 gilt für DIESEN Build als Verdikt.
// Ohne das bliebe pro Seitenaufruf genau eine vorhersehbare Fehlermeldung in
// der Konsole (Alex, 18.08.: „bekomme das noch immer"). Schlüssel enthält die
// Build-Nummer — ein neuer Portal-Stand probiert automatisch erneut, das
// Verdikt kann also nie eine später gebaute Kontingent-Anzeige verdecken.
const MISSING_KEY = `cloud-tenant-usage-missing:${__BUILD_ID__}`;
try {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(MISSING_KEY) === '1') {
    endpointMissing = true;
  }
} catch { /* private mode */ }

export function useTenantUsage(): TenantUsage | null {
  const [usage, setUsage] = useState<TenantUsage | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const load = async () => {
      if (endpointMissing) return;
      try {
        await instanceReady();
        // Nur fragen, wenn die Instanz den Kontingent-Vertrag MELDET. Ohne
        // Meldung existiert der Endpunkt nicht — dann gibt es auch keine
        // Anfrage und kein 404 (Alex, 18.08.). Meldet ihn eine Instanz, trägt
        // die erste Antwort die Anzeige, ohne dass jemand etwas umstellt.
        if (!instanceHas('tenant_usage')) {
          endpointMissing = true;
          if (timer) window.clearInterval(timer);
          if (!cancelled) setUsage(null);
          return;
        }
        const res = await fetch('/api/tenant/usage', { headers: authHeader() });
        if (cancelled) return;
        if (res.status === 404) {
          endpointMissing = true;   // Vertrag hier nicht erfüllt → nie wieder fragen
          try { localStorage.setItem(MISSING_KEY, '1'); } catch { /* private mode */ }
          setUsage(null);
          if (timer) window.clearInterval(timer);
          return;
        }
        if (!res.ok) { setUsage(null); return; }
        const body = await res.json() as Partial<TenantUsage>;
        if (typeof body.included_units !== 'number' || typeof body.used_units !== 'number') {
          setUsage(null);
          return;
        }
        setUsage({
          included_units: body.included_units,
          used_units: body.used_units,
          resets_at: String(body.resets_at ?? ''),
          throttled: body.throttled === true,
        });
      } catch { if (!cancelled) setUsage(null); }
    };
    void load();
    timer = window.setInterval(load, POLL_MS);
    return () => { cancelled = true; if (timer) window.clearInterval(timer); };
  }, []);
  return usage;
}
